import React, { useEffect, useState } from 'react';

import Loading from 'assets/icons/Loading';
import Paragraph from 'components/text/Paragraph';
import PropTypes from 'prop-types';
import { TextLinkWysisyg } from 'components/text/TextLink';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import colors from 'config/theme/colors';
import styled from 'libs/styled';
import transitions from 'config/theme/transitions';
import { wysiwygProp } from 'utils/proptypes/modules/textProps';

const Wrapper = styled('div', { shouldForwardProp: prop => ['isLoading'].indexOf(prop) === -1 })`
    position: relative;

    svg {
        display: none;
    }

    ${({ isLoading }) =>
        isLoading &&
        `
        > *:not(svg) {
            opacity: 0.5;
        }

        svg {
            display: block;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }
    `}
`;

const Label = styled('label')`
    position: relative;
    display: flex;
    align-items: flex-start;
    padding-left: 32px;
    font-size: 16px;
    line-height: 1.75;

    p {
        margin: 0;
    }

    input:checked ~ span {
        background-color: ${colors.black};
        border: 1px solid ${colors.black};
        box-shadow: inset 0 0 0 2px ${colors.white};
    }
`;

const Input = styled('input')`
    display: none;
`;

const RadioMarker = styled('span')`
    display: block;
    position: absolute;
    top: 14px; /* Align to the middle of the first row */
    left: 0px;
    width: 16px;
    height: 16px; /* Will be same size as the text */
    transform: translateY(-50%);
    border: 1px solid ${colors.black};
`;

const ErrorMessage = styled(Paragraph, { shouldForwardProp: prop => ['error'].indexOf(prop) === -1 })`
    color: ${colors.error};
    font-size: 10px;
    margin: 0;
    display: ${({ error }) => (error ? 'block' : 'none')};
    transition: opacity ${transitions.secondary};
`;

const StyledParagraph = styled(Paragraph)`
    a {
        color: inherit;
    }
`;

const Radio = ({ error, items, className, handleChange = () => {}, name, isLoading = false, defaultActive }) => {
    const [active, setActive] = useState(defaultActive);
    const [prevDefault, setPrevDefault] = useState(defaultActive);

    // Make sure that active is updated if defaultActive changes
    useEffect(() => {
        if (defaultActive !== prevDefault) {
            setPrevDefault(defaultActive);
            setActive(defaultActive);
        }
    }, [defaultActive]);

    const change = item => {
        setActive(item.id);
        handleChange(item);
    };

    return (
        <Wrapper className={className} isLoading={isLoading}>
            {items &&
                items.length > 0 &&
                items.map(item => (
                    <Label key={item.id}>
                        <Wysiwyg
                            data={item.text}
                            textComponent={props => <StyledParagraph {...props} />}
                            tagComponents={{ url: TextLinkWysisyg }}
                        />
                        <Input
                            type="radio"
                            name={name}
                            disabled={isLoading}
                            checked={active === item.id}
                            onChange={() => change(item)}
                        />
                        <RadioMarker />
                    </Label>
                ))}
            <ErrorMessage error={!!error}>*{error}</ErrorMessage>
            <Loading />
        </Wrapper>
    );
};

Radio.propTypes = {
    className: PropTypes.string,
    defaultActive: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    error: PropTypes.string,
    handleChange: PropTypes.func,
    isLoading: PropTypes.bool,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            text: wysiwygProp.isRequired,
        })
    ).isRequired,
    name: PropTypes.string.isRequired,
};

export default Radio;
